import { GatsbyImage, getImage } from "gatsby-plugin-image"
import PropTypes from "prop-types"
import React from "react"

export const isUrl = (data) =>
  typeof data === "string" && /https?:\/\//.test(data)

export const isLocalPath = (data) =>
  typeof data === "string" && data.startsWith("/")

export const isRelativePath = (data) =>
  typeof data === "string" && data.startsWith("./")

export const isSVG = (data) => !!data.url && typeof data.url === "string"

function Image(props) {
  const { className, src, alt, loading = "lazy", ...rest } = props

  if (!src) {
    throw new Error("There is no image source")
  }

  const imageProps = {
    className,
    ...rest,
  }

  // External URL, local static path, or relative path
  if (isUrl(src) || isLocalPath(src) || isRelativePath(src)) {
    imageProps.src = src
  }
  // Contentful SVG asset or local path object (has url property)
  else if (isSVG(src)) {
    imageProps.src = src.url
  }
  // Contentful image asset
  else {
    imageProps.src = ""
    imageProps.image = getImage(src)
  }

  // Determine alt text with fallback to Contentful description
  const resolvedAlt =
    isUrl(src) || isLocalPath(src) || isRelativePath(src)
      ? alt
      : alt || src?.description

  // Warn in development if alt text is missing
  if (
    process.env.NODE_ENV === "development" &&
    !resolvedAlt &&
    resolvedAlt !== ""
  ) {
    console.warn(
      `[Image] Missing alt text for image: ${imageProps.src || "gatsby-image"}`
    )
  }

  imageProps.alt = resolvedAlt || ""

  const ImageComponent =
    isUrl(src) || isLocalPath(src) || isRelativePath(src) || isSVG(src)
      ? "img"
      : GatsbyImage

  if (ImageComponent === "img") {
    delete imageProps.objectFit
    // Add native lazy loading for img tags (GatsbyImage handles this automatically)
    imageProps.loading = loading
  }

  return <ImageComponent {...imageProps} />
}

Image.propTypes = {
  /** Additional className(s) */
  className: PropTypes.string,
  /** Gatsby image data */
  src: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  /** Alternative text for image */
  alt: PropTypes.string,
  /** Object fit ("contain", "cover", "fill") */
  objectFit: PropTypes.string,
  /** Native lazy loading ("lazy", "eager", "auto") - defaults to "lazy" */
  loading: PropTypes.oneOf(["lazy", "eager", "auto"]),
}

export default Image
